/**
 * Web Audio Engine for MIDI audition, metronome, and polyphonic playback
 * Supports sample-accurate lookahead scheduling, dynamics limiter, and external AudioContext
 */

import { InstrumentId, InstrumentStatus, PianoEffectsSettings } from '../audio/types';
import { InstrumentManager } from '../audio/InstrumentManager';

export class AudioEngine {
  private ctx: AudioContext | null = null;
  private isExternalCtx = false;
  private masterGain: GainNode | null = null;
  private masterLimiter: DynamicsCompressorNode | null = null;
  private instrumentManager: InstrumentManager | null = null;

  // Active live voices (keyed by pitch, for live MIDI key presses)
  private activeLiveVoices = new Map<number, { stop: (stopTime?: number) => void }>();

  // Active scheduled playback voices (for playback track notes)
  private activeScheduledVoices = new Map<number, { stop: () => void }>();

  constructor(externalCtx?: AudioContext | null) {
    if (externalCtx) {
      this.ctx = externalCtx;
      this.isExternalCtx = true;
    }
  }

  public setExternalContext(externalCtx: AudioContext | null): void {
    if (externalCtx && externalCtx !== this.ctx) {
      if (this.ctx && !this.isExternalCtx) {
        try {
          this.ctx.close();
        } catch {
          // ignore
        }
      }
      this.ctx = externalCtx;
      this.isExternalCtx = true;
      this.masterGain = null;
      this.masterLimiter = null;
      // Instruments (and their loaded samples) were built against the old
      // context — force recreation so playback binds to the new one instead
      // of silently targeting a stale/closed AudioContext.
      this.instrumentManager = null;
    }
  }

  public getContext(): AudioContext {
    if (!this.ctx) {
      const AudioCtxClass =
        window.AudioContext ||
        (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      this.ctx = new AudioCtxClass();
      this.isExternalCtx = false;
    }
    if (this.ctx.state === 'suspended') {
      this.ctx.resume().catch(() => {});
    }
    return this.ctx;
  }

  public async resume(): Promise<AudioContext> {
    const ctx = this.getContext();
    if (ctx.state === 'suspended') {
      try {
        await ctx.resume();
      } catch {
        // ignore
      }
    }
    return ctx;
  }

  /**
   * Master output chain with dedicated brickwall limiter to eliminate digital clipping
   */
  private getMaster(): GainNode {
    const ctx = this.getContext();
    if (!this.masterGain) {
      this.masterGain = ctx.createGain();
      this.masterGain.gain.setValueAtTime(0.75, ctx.currentTime);

      // Dynamics Compressor acting as a transparent master limiter
      this.masterLimiter = ctx.createDynamicsCompressor();
      this.masterLimiter.threshold.setValueAtTime(-1.0, ctx.currentTime);
      this.masterLimiter.knee.setValueAtTime(0.0, ctx.currentTime);
      this.masterLimiter.ratio.setValueAtTime(20.0, ctx.currentTime);
      this.masterLimiter.attack.setValueAtTime(0.002, ctx.currentTime);
      this.masterLimiter.release.setValueAtTime(0.05, ctx.currentTime);

      this.masterGain.connect(this.masterLimiter);
      this.masterLimiter.connect(ctx.destination);
    }
    return this.masterGain;
  }

  private getInstrumentManager(): InstrumentManager {
    if (!this.instrumentManager) {
      const ctx = this.getContext();
      const master = this.getMaster();
      this.instrumentManager = new InstrumentManager(ctx, master);
    }
    return this.instrumentManager;
  }

  public getInstrumentId(): InstrumentId {
    return this.getInstrumentManager().getCurrentInstrumentId();
  }

  public setInstrument(id: InstrumentId): void {
    this.getInstrumentManager().setInstrument(id);
  }

  public getInstrumentStatus(id: InstrumentId): InstrumentStatus {
    return this.getInstrumentManager().getStatus(id);
  }

  public onInstrumentStatusChange(listener: (status: InstrumentStatus) => void): () => void {
    return this.getInstrumentManager().subscribe(listener);
  }

  public getEffectsSettings(): PianoEffectsSettings {
    return this.getInstrumentManager().getEffectsSettings();
  }

  public updateEffectsSettings(settings: Partial<PianoEffectsSettings>): void {
    this.getInstrumentManager().updateEffectsSettings(settings);
  }

  public onEffectsSettingsChange(listener: (settings: PianoEffectsSettings) => void): () => void {
    return this.getInstrumentManager().subscribeEffects(listener);
  }

  /**
   * Sample-accurate scheduled note for playback engine
   */
  public scheduleNote(
    pitch: number,
    velocity: number = 90,
    startTime: number,
    durationSeconds: number = 0.5
  ): void {
    this.getInstrumentManager().scheduleNote(pitch, velocity, startTime, durationSeconds);
  }

  /**
   * Play live Note On (for interactive keyboard playing)
   * Returns a release function to smoothly stop when key is released
   */
  public playNoteOn(pitch: number, velocity: number = 90, scheduledTime?: number): (stopTime?: number) => void {
    return this.getInstrumentManager().playNoteOn(pitch, velocity, scheduledTime);
  }

  /**
   * Helper to play note for fixed duration (delegates to scheduleNote)
   */
  public playNote(pitch: number, velocity: number = 90, durationSeconds: number = 0.5, startTime?: number): void {
    const ctx = this.getContext();
    const start = startTime !== undefined ? startTime : ctx.currentTime;
    this.scheduleNote(pitch, velocity, start, durationSeconds);
  }

  /**
   * Clean studio metronome click (woodblock timbre)
   * Downbeat (beat 1): 1600Hz -> 700Hz sweep, peak gain 0.20
   * Offbeats: 1000Hz -> 500Hz sweep, peak gain 0.12
   */
  public playClick(isFirstBeat: boolean, time?: number): void {
    const ctx = this.getContext();
    const t = time !== undefined ? time : ctx.currentTime;

    const osc = ctx.createOscillator();
    const gain = ctx.createGain();

    osc.type = isFirstBeat ? 'triangle' : 'sine';
    osc.frequency.setValueAtTime(isFirstBeat ? 1600 : 1050, t);
    osc.frequency.exponentialRampToValueAtTime(isFirstBeat ? 700 : 550, t + 0.025);

    const clickGain = isFirstBeat ? 0.20 : 0.12;
    gain.gain.setValueAtTime(0.0001, t);
    gain.gain.linearRampToValueAtTime(clickGain, t + 0.001);
    gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.035);

    osc.connect(gain);
    gain.connect(this.getMaster());

    osc.start(t);
    osc.stop(t + 0.04);
  }

  /**
   * Release and clear all currently sounding notes and scheduled voices
   */
  public stopAll(): void {
    const ctx = this.getContext();
    const now = ctx.currentTime;

    try {
      this.instrumentManager?.stopAll();
    } catch {
      // ignore
    }

    // Stop all live held notes
    for (const voice of this.activeLiveVoices.values()) {
      try {
        voice.stop(now);
      } catch {
        // ignore
      }
    }
    this.activeLiveVoices.clear();

    // Stop all scheduled playback notes
    for (const voice of this.activeScheduledVoices.values()) {
      try {
        voice.stop();
      } catch {
        // ignore
      }
    }
    this.activeScheduledVoices.clear();
  }

  public isSuspended(): boolean {
    return this.ctx?.state === 'suspended';
  }

  /** Whether a context (internal or external) has already been assigned/created. */
  public hasContext(): boolean {
    return this.ctx !== null;
  }

  public async resumeContext(): Promise<void> {
    const ctx = this.getContext();
    if (ctx.state === 'suspended') {
      await ctx.resume().catch(() => {});
    }
  }
}

export const globalAudioEngine = new AudioEngine();

// Auto-unlock Web Audio on first user interaction anywhere in document — but only if a context
// already exists. Calling resumeContext() unconditionally would call getContext(), which EAGERLY
// CREATES a throwaway internal AudioContext from ANY click anywhere on the site (even the landing
// page, long before the Piano Recorder is ever opened). That context is unrelated to the Editor's
// shared one and later gets discarded/closed once MidiDawModal assigns the real external context —
// wasted work at best, a source of confusing races at worst. Once Piano Rec is actually open,
// hasContext() is true and this keeps doing its real job (resuming a context the browser suspended).
if (typeof window !== 'undefined') {
  const unlockAudio = () => {
    if (globalAudioEngine.hasContext()) {
      globalAudioEngine.resumeContext().catch(() => {});
    }
  };
  window.addEventListener('pointerdown', unlockAudio, { passive: true, capture: true });
  window.addEventListener('keydown', unlockAudio, { passive: true, capture: true });
  window.addEventListener('touchstart', unlockAudio, { passive: true, capture: true });
}
